import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ChangeDetectionStrategy, Input, HostBinding, Output, HostListener, TemplateRef, Optional, ViewChild, ContentChild, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i2$1 from '@angular/cdk/drag-drop';
import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import * as i1$1 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faEdit, faTrash, faCheck, faArrowUp, faSortAlphaDown, faMinusCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import DOMPurify from 'dompurify';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

class EditableComponent {
    constructor(renderer, elementRef) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.editable = false;
        this.selectOnEdit = false;
        this.focusOnEdit = false;
        this.placeholder = '';
        this.highlight = '';
        this.parse = (str) => str;
        this.enter = new EventEmitter();
        this.escape = new EventEmitter();
        this.newLine = new EventEmitter();
        this.spellcheck = true;
        this.draggable = false;
        this.transform = (str) => DOMPurify.sanitize(str);
    }
    ngOnChanges(changes) {
        if ('editable' in changes) {
            const editing = changes['editable'];
            this.setEditable(editing.currentValue);
        }
        if ('highlight' in changes) {
            this.render(this.value);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        if (this.value !== value) {
            this.value = value;
        }
        this.render(value);
    }
    onKey(event) {
        if (event.code === "Enter") {
            this.enter.emit(event);
        }
        else if (event.code === "Escape") {
            this.escape.emit(event);
        }
    }
    onAfterKey(event) {
        const value = this.getElementValue();
        this.onChange(value);
        this.onTouched();
    }
    onMouseDown(event) {
        if (this.editable) {
            event.stopPropagation();
        }
    }
    onBlur(event) {
        const value = this.getElementValue();
        this.render(value);
    }
    getElementValue() {
        return this.parse(this.elementRef.nativeElement.innerText);
    }
    render(value) {
        if (!this.editable) {
            this.elementRef.nativeElement.innerHTML = this.transform(value);
        }
        else {
            this.elementRef.nativeElement.innerText = value;
        }
    }
    setEditable(editable) {
        const element = this.elementRef.nativeElement;
        if (editable) {
            const value = this.getElementValue();
            this.render(value);
            this.renderer.setAttribute(element, 'contenteditable', 'true');
            if (this.focusOnEdit) {
                this.placeCaretAtEnd(element);
            }
            if (this.selectOnEdit) {
                this.makeSelection(element);
            }
        }
        else {
            this.renderer.setAttribute(element, 'contenteditable', 'false');
            this.renderer.setProperty(element, 'scrollLeft', 0);
        }
    }
    getPreCaretText(element) {
        element.focus();
        if (window.getSelection().rangeCount === 0) {
            return '';
        }
        const range = window.getSelection().getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString();
    }
    placeCaretAtEnd(element) {
        element.focus();
        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        setTimeout(() => {
            sel.addRange(range);
        });
    }
    makeSelection(element) {
        const range = document.createRange(), selection = window.getSelection();
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        // some of the selection/focus DOM APIs need timeout to work with Angular native elements in Safari
        // https://www.codeproject.com/Questions/5278674/Focus-and-nativeelement-focus-not-working-in-angul
        // https://pretagteam.com/question/nativeelementselect-only-works-on-a-settimeout-angular
        setTimeout(() => {
            selection.addRange(range);
            document.execCommand('selectAll', false, null);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: EditableComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: EditableComponent, selector: "rcp-token-field-editable-input", inputs: { editable: "editable", selectOnEdit: "selectOnEdit", focusOnEdit: "focusOnEdit", placeholder: "placeholder", highlight: "highlight", transform: "transform", parse: "parse", spellcheck: "spellcheck" }, outputs: { enter: "enter", escape: "escape", newLine: "newLine" }, host: { listeners: { "keydown": "onKey($event)", "keyup": "onAfterKey($event)", "mousedown": "onMouseDown($event)", "blur": "onBlur($event)" }, properties: { "class.editable": "this.editable", "spellcheck": "this.spellcheck", "draggable": "this.draggable" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => EditableComponent),
                multi: true
            }
        ], usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:block;width:100%;height:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;-webkit-user-select:none;user-select:none}:host.editable{-webkit-user-select:text;user-select:text}:host[contenteditable=true]{white-space:pre-wrap;word-wrap:break-word;overflow-wrap:break-word}:host[contenteditable=true]:empty:before{content:attr(placeholder);display:block}:host:focus{outline:0px solid transparent;text-overflow:clip;cursor:text}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: EditableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rcp-token-field-editable-input', template: '', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => EditableComponent),
                            multi: true
                        }
                    ], styles: [":host{display:block;width:100%;height:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;-webkit-user-select:none;user-select:none}:host.editable{-webkit-user-select:text;user-select:text}:host[contenteditable=true]{white-space:pre-wrap;word-wrap:break-word;overflow-wrap:break-word}:host[contenteditable=true]:empty:before{content:attr(placeholder);display:block}:host:focus{outline:0px solid transparent;text-overflow:clip;cursor:text}\n"] }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.ElementRef }], propDecorators: { editable: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.editable']
            }], selectOnEdit: [{
                type: Input
            }], focusOnEdit: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], highlight: [{
                type: Input
            }], transform: [{
                type: Input
            }], parse: [{
                type: Input
            }], enter: [{
                type: Output
            }], escape: [{
                type: Output
            }], newLine: [{
                type: Output
            }], spellcheck: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['spellcheck']
            }], draggable: [{
                type: HostBinding,
                args: ['draggable']
            }], onKey: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onAfterKey: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onMouseDown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }] } });

class TokenFieldComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.disabled = false;
        this.text = '';
        this.focusFirst = true;
        this.delimiter = ',';
        this.placeholder = 'Type here...';
        this.editable = true;
        this.maxLength = null;
        this.errors = [];
        this.textChange = new EventEmitter();
        this.click = new EventEmitter();
        this.value = [];
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    focus() {
        this.inputRef.nativeElement.focus();
    }
    getError(index) {
        return this.errors.find(err => err.entries.includes(index));
    }
    removeEntry(index) {
        if (this.disabled) {
            return;
        }
        this.value.splice(index, 1);
        this.onChange(this.value);
        this.onTouched();
    }
    addEntry(item) {
        if (this.disabled) {
            return;
        }
        if (this.delimiter) {
            const tmp = item.split(this.delimiter).map(s => s.trim()).filter(v => !!v);
            this.value = this.value.concat(tmp);
        }
        else {
            this.value.push(item);
        }
        this.text = '';
        this.onChange(this.value);
        this.onTouched();
    }
    onSelectItem(event) {
        this.addEntry(event.item);
        event.preventDefault();
    }
    onEntryClick(index) {
        this.click.next(index);
    }
    onKeyUpEnter(event) {
        const text = this.text?.trim();
        if (text) {
            this.addEntry(text);
            this.focus();
        }
    }
    onKeyDownEnter(event) {
        const text = this.text?.trim();
        if (text) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    onBlur(event) {
        const text = this.text?.trim();
        if (text) {
            this.addEntry(text);
        }
    }
    inputFormatter(item) {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: TokenFieldComponent, selector: "rcp-token-field", inputs: { disabled: "disabled", text: "text", focusFirst: "focusFirst", delimiter: "delimiter", placeholder: "placeholder", editable: "editable", maxLength: "maxLength", errors: "errors", search: "search" }, outputs: { textChange: "textChange", click: "click" }, host: { listeners: { "click": "focus()" }, properties: { "class.disabled": "this.disabled" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TokenFieldComponent),
                multi: true
            }
        ], queries: [{ propertyName: "resultTemplateRef", first: true, predicate: ["resultTemplate"], descendants: true, read: TemplateRef, static: true }, { propertyName: "tokenTemplateRef", first: true, predicate: ["tokenTemplate"], descendants: true, read: TemplateRef, static: true }], viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: "<div *ngFor=\"let item of value; let i = index;\"\n  [ngClass]=\"{'error': getError(i), 'token': true}\"\n  [ngbTooltip]=\"getError(i)?.message\"\n  (click)=\"onEntryClick(i)\">\n  <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n  <span class=\"addon-btn\" (click)=\"removeEntry(i)\" ngbTooltip=\"Click to remove\" openDelay=\"1000\" container=\"body\">\n    <fa-icon [icon]=\"['fal', 'times']\"></fa-icon>\n  </span>\n</div>\n\n<ng-container *ngIf=\"search; else simpleInput\">\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [ngbTypeahead]=\"search\"\n    [resultTemplate]=\"resultTemplateRef\"\n    popupClass=\"papers-scrollbar\"\n    [focusFirst]=\"focusFirst\"\n    [editable]=\"editable\"\n    [disabled]=\"disabled\"\n    [inputFormatter]=\"inputFormatter\"\n    (selectItem)=\"onSelectItem($event)\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxLength\">\n</ng-container>\n\n<ng-template #simpleInput>\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [attr.maxlength]=\"maxLength\">\n</ng-template>", styles: [":host{display:inline-table!important;height:auto!important;min-height:66px}:host.disabled{opacity:.4}input{border:0;font-size:inherit;color:inherit;background-color:transparent;padding:5px 2px;width:100%;outline:none}.token{display:inline-block;font-size:inherit;font-weight:inherit;padding:.5em 1.6em .5em .75em;margin:.25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:default;max-width:200px;overflow:hidden;text-overflow:ellipsis;position:relative;white-space:nowrap;transition:background-color .2s linear}.token .addon-btn{position:absolute;right:.75em;top:.55em;cursor:pointer}:host.disabled .token .addon-btn{cursor:not-allowed}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i1$1.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "spin", "pulse", "mask", "styles", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "classes", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rcp-token-field', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TokenFieldComponent),
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div *ngFor=\"let item of value; let i = index;\"\n  [ngClass]=\"{'error': getError(i), 'token': true}\"\n  [ngbTooltip]=\"getError(i)?.message\"\n  (click)=\"onEntryClick(i)\">\n  <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n  <span class=\"addon-btn\" (click)=\"removeEntry(i)\" ngbTooltip=\"Click to remove\" openDelay=\"1000\" container=\"body\">\n    <fa-icon [icon]=\"['fal', 'times']\"></fa-icon>\n  </span>\n</div>\n\n<ng-container *ngIf=\"search; else simpleInput\">\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [ngbTypeahead]=\"search\"\n    [resultTemplate]=\"resultTemplateRef\"\n    popupClass=\"papers-scrollbar\"\n    [focusFirst]=\"focusFirst\"\n    [editable]=\"editable\"\n    [disabled]=\"disabled\"\n    [inputFormatter]=\"inputFormatter\"\n    (selectItem)=\"onSelectItem($event)\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxLength\">\n</ng-container>\n\n<ng-template #simpleInput>\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [attr.maxlength]=\"maxLength\">\n</ng-template>", styles: [":host{display:inline-table!important;height:auto!important;min-height:66px}:host.disabled{opacity:.4}input{border:0;font-size:inherit;color:inherit;background-color:transparent;padding:5px 2px;width:100%;outline:none}.token{display:inline-block;font-size:inherit;font-weight:inherit;padding:.5em 1.6em .5em .75em;margin:.25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:default;max-width:200px;overflow:hidden;text-overflow:ellipsis;position:relative;white-space:nowrap;transition:background-color .2s linear}.token .addon-btn{position:absolute;right:.75em;top:.55em;cursor:pointer}:host.disabled .token .addon-btn{cursor:not-allowed}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], text: [{
                type: Input
            }], focusFirst: [{
                type: Input
            }], delimiter: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], editable: [{
                type: Input
            }, {
                type: Optional
            }], maxLength: [{
                type: Input
            }, {
                type: Optional
            }], errors: [{
                type: Input
            }, {
                type: Optional
            }], search: [{
                type: Input
            }, {
                type: Optional
            }], textChange: [{
                type: Output
            }], click: [{
                type: Output
            }], inputRef: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }], resultTemplateRef: [{
                type: ContentChild,
                args: ['resultTemplate', { read: TemplateRef, static: true }]
            }], tokenTemplateRef: [{
                type: ContentChild,
                args: ['tokenTemplate', { read: TemplateRef, static: true }]
            }], focus: [{
                type: HostListener,
                args: ['click']
            }] } });

class TokenFieldPickerComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.disabled = false;
        this.items = [];
        this.value = [];
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    addEntry(index) {
        if (this.disabled) {
            return;
        }
        const item = this.items[index];
        this.items.splice(index, 1);
        this.value.push(item);
        this.onChange(this.value);
        this.onTouched();
    }
    removeEntry(index) {
        if (this.disabled) {
            return;
        }
        const item = this.value[index];
        this.value.splice(index, 1);
        this.items.push(item);
        this.onChange(this.value);
        this.onTouched();
    }
    onOrderChange(event) {
        moveItemInArray(this.value, event.previousIndex, event.currentIndex);
        this.onChange(this.value);
        this.onTouched();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldPickerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: TokenFieldPickerComponent, selector: "rcp-token-field-picker", inputs: { disabled: "disabled", items: "items" }, host: { properties: { "class.disabled": "this.disabled" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TokenFieldPickerComponent),
                multi: true
            }
        ], queries: [{ propertyName: "tokenTemplateRef", first: true, predicate: ["tokenTemplate"], descendants: true, read: TemplateRef, static: true }], ngImport: i0, template: "<div class=\"field available\" *ngIf=\"items.length\">\n  <div class=\"label\">Available:</div>\n  <div class=\"tokens\">\n    <div class=\"token\" *ngFor=\"let item of items; let i = index;\">\n      <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n      <span class=\"addon-btn\" (click)=\"addEntry(i)\" [ngbTooltip]=\"!disabled ? 'Click to add' : ''\" openDelay=\"1000\" container=\"body\">\n        &nbsp;<fa-icon [icon]=\"['fal', 'plus-circle']\"></fa-icon>\n      </span>\n    </div>\n  </div>\n</div>\n\n\n<div class=\"field selected\" *ngIf=\"value.length\">\n  <div class=\"label\">Selected:</div>\n  <div class=\"tokens\">\n    <div class=\"sortable\"\n      cdkDropList\n      cdkDropListLockAxis=\"x\"\n      cdkDropListOrientation=\"horizontal\"\n      [cdkDropListDisabled]=\"disabled || value.length < 2\"\n      (cdkDropListDropped)=\"onOrderChange($event)\">\n\n      <div class=\"token\" cdkDrag cdkDragBoundary=\".sortable\" *ngFor=\"let item of value; let i = index;\">\n        <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n        <span class=\"addon-btn\" (click)=\"removeEntry(i)\" [ngbTooltip]=\"!disabled ? 'Click to remove' : ''\" openDelay=\"1000\" container=\"body\">\n          &nbsp;<fa-icon [icon]=\"['fal', 'minus-circle']\"></fa-icon>\n        </span>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:inline-table!important;height:auto!important}:host.disabled{opacity:.4}.field{display:flex;flex-direction:row}.field.selected{margin-top:5px;padding-top:5px}.field .label{flex:0 0 70px;line-height:38px;vertical-align:middle}.field .tokens{flex:1 0}.sortable{display:inline-block}.token{display:inline-block;font-size:inherit;font-weight:inherit;padding:.5em .75em;margin:.25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:default;transition:background-color .2s linear}.token .addon-btn{display:inline-block;margin-left:.25em;cursor:pointer}:host.disabled .token .addon-btn{cursor:not-allowed}.sortable .token:not(.cdk-drag-disabled){cursor:move}.cdk-drag-preview{box-sizing:border-box;opacity:.6}.cdk-drag-placeholder{opacity:0}.cdk-drag-animating{transition:transform .25s cubic-bezier(0,0,.2,1)}.sortable.cdk-drop-list-dragging .cell:not(.cdk-drag-placeholder){transition:transform .25s cubic-bezier(0,0,.2,1)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2$1.CdkDropList, selector: "[cdkDropList], cdk-drop-list", inputs: ["cdkDropListConnectedTo", "cdkDropListData", "cdkDropListOrientation", "id", "cdkDropListLockAxis", "cdkDropListDisabled", "cdkDropListSortingDisabled", "cdkDropListEnterPredicate", "cdkDropListSortPredicate", "cdkDropListAutoScrollDisabled", "cdkDropListAutoScrollStep"], outputs: ["cdkDropListDropped", "cdkDropListEntered", "cdkDropListExited", "cdkDropListSorted"], exportAs: ["cdkDropList"] }, { kind: "directive", type: i2$1.CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "component", type: i1$1.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "spin", "pulse", "mask", "styles", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "classes", "transform", "a11yRole"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rcp-token-field-picker', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TokenFieldPickerComponent),
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"field available\" *ngIf=\"items.length\">\n  <div class=\"label\">Available:</div>\n  <div class=\"tokens\">\n    <div class=\"token\" *ngFor=\"let item of items; let i = index;\">\n      <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n      <span class=\"addon-btn\" (click)=\"addEntry(i)\" [ngbTooltip]=\"!disabled ? 'Click to add' : ''\" openDelay=\"1000\" container=\"body\">\n        &nbsp;<fa-icon [icon]=\"['fal', 'plus-circle']\"></fa-icon>\n      </span>\n    </div>\n  </div>\n</div>\n\n\n<div class=\"field selected\" *ngIf=\"value.length\">\n  <div class=\"label\">Selected:</div>\n  <div class=\"tokens\">\n    <div class=\"sortable\"\n      cdkDropList\n      cdkDropListLockAxis=\"x\"\n      cdkDropListOrientation=\"horizontal\"\n      [cdkDropListDisabled]=\"disabled || value.length < 2\"\n      (cdkDropListDropped)=\"onOrderChange($event)\">\n\n      <div class=\"token\" cdkDrag cdkDragBoundary=\".sortable\" *ngFor=\"let item of value; let i = index;\">\n        <ng-template [ngTemplateOutlet]=\"tokenTemplateRef\" [ngTemplateOutletContext]=\"{ item: item }\"></ng-template>\n        <span class=\"addon-btn\" (click)=\"removeEntry(i)\" [ngbTooltip]=\"!disabled ? 'Click to remove' : ''\" openDelay=\"1000\" container=\"body\">\n          &nbsp;<fa-icon [icon]=\"['fal', 'minus-circle']\"></fa-icon>\n        </span>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [":host{display:inline-table!important;height:auto!important}:host.disabled{opacity:.4}.field{display:flex;flex-direction:row}.field.selected{margin-top:5px;padding-top:5px}.field .label{flex:0 0 70px;line-height:38px;vertical-align:middle}.field .tokens{flex:1 0}.sortable{display:inline-block}.token{display:inline-block;font-size:inherit;font-weight:inherit;padding:.5em .75em;margin:.25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:default;transition:background-color .2s linear}.token .addon-btn{display:inline-block;margin-left:.25em;cursor:pointer}:host.disabled .token .addon-btn{cursor:not-allowed}.sortable .token:not(.cdk-drag-disabled){cursor:move}.cdk-drag-preview{box-sizing:border-box;opacity:.6}.cdk-drag-placeholder{opacity:0}.cdk-drag-animating{transition:transform .25s cubic-bezier(0,0,.2,1)}.sortable.cdk-drop-list-dragging .cell:not(.cdk-drag-placeholder){transition:transform .25s cubic-bezier(0,0,.2,1)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], items: [{
                type: Input
            }], tokenTemplateRef: [{
                type: ContentChild,
                args: ['tokenTemplate', { read: TemplateRef, static: true }]
            }] } });

class TokenFieldEditableComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.disabled = false;
        this.text = '';
        this.focusFirst = true;
        this.delimiter = ',';
        this.placeholder = 'Type here...';
        this.label = '';
        this.maxVisibleElements = 7;
        this.canMoveToTop = false;
        this.canSortAlpha = false;
        this.textChange = new EventEmitter();
        this.value = [];
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    focus() {
        this.inputRef.nativeElement.focus();
    }
    onEditEnter(event) {
        this.saveEntry(this.editIndex);
        event.preventDefault();
    }
    saveEntry(i) {
        this.value[this.editIndex] = this.editValue;
        this.editIndex = null;
        this.onChange(this.value);
        this.onTouched();
    }
    editEntry(index) {
        this.editValue = this.value[index];
        this.editIndex = index;
    }
    removeEntry(index) {
        if (this.disabled) {
            return;
        }
        this.value.splice(index, 1);
        this.onChange(this.value);
        this.onTouched();
    }
    moveToTop(index) {
        this.editIndex = null;
        moveItemInArray(this.value, index, 0);
        if (this.virtualScrollViewport) {
            this.virtualScrollViewport.scrollTo({ top: 0, behavior: 'smooth' });
        }
        else {
            this.viewport.nativeElement.scrollTop = 0;
        }
        this.onChange(this.value);
        this.onTouched();
    }
    sortAlpha() {
        this.value.sort((a, b) => {
            const alc = a.toLocaleLowerCase();
            const blc = b.toLocaleLowerCase();
            if (alc < blc)
                return -1;
            if (alc > blc)
                return 1;
            return 0;
        });
        this.onChange(this.value);
        this.onTouched();
    }
    addEntry(item) {
        if (this.disabled) {
            return;
        }
        if (this.delimiter) {
            const tmp = item.split(this.delimiter).map(s => s.trim());
            this.value = this.value.concat(tmp);
        }
        else {
            this.value.push(item);
        }
        this.text = '';
        setTimeout(() => {
            if (this.virtualScrollViewport) {
                this.virtualScrollViewport.scrollTo({ bottom: 0, behavior: 'smooth' });
            }
            else {
                this.viewport.nativeElement.scrollTop = this.viewport.nativeElement.scrollHeight + 10;
            }
        }, 50);
        this.onChange(this.value);
        this.onTouched();
    }
    getViewportHeight() {
        const itemsHeight = Math.min(this.maxVisibleElements, this.value.length) * (30 + 2);
        return itemsHeight ? itemsHeight + 10 : 0;
    }
    onSelectItem(event) {
        this.addEntry(event.item);
        event.preventDefault();
    }
    onKeyUpEnter(event) {
        const text = this.text.trim();
        if (text) {
            this.addEntry(text);
            this.focus();
        }
    }
    onKeyDownEnter(event) {
        const text = this.text.trim();
        if (text) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    onBlur(event) {
        const text = this.text.trim();
        if (text) {
            this.addEntry(text);
        }
    }
    onOrderChange(event) {
        this.editIndex = null;
        const fromIndex = event.previousIndex;
        const toIndex = event.currentIndex;
        moveItemInArray(this.value, fromIndex, toIndex);
        this.onChange(this.value);
        this.onTouched();
    }
    inputFormatter(item) {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldEditableComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: TokenFieldEditableComponent, selector: "rcp-token-field-editable", inputs: { disabled: "disabled", text: "text", focusFirst: "focusFirst", delimiter: "delimiter", placeholder: "placeholder", label: "label", maxVisibleElements: "maxVisibleElements", canMoveToTop: "canMoveToTop", canSortAlpha: "canSortAlpha", search: "search" }, outputs: { textChange: "textChange" }, host: { listeners: { "click": "focus()" }, properties: { "class.disabled": "this.disabled" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TokenFieldEditableComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["input"], descendants: true }, { propertyName: "virtualScrollViewport", first: true, predicate: CdkVirtualScrollViewport, descendants: true, static: true }, { propertyName: "viewport", first: true, predicate: ["viewport"], descendants: true, static: true }], ngImport: i0, template: "\n<ng-template #typeaheadResultTemplate let-result=\"result\" let-term=\"term\">\n  <span>{{result}}</span>\n</ng-template>\n\n<label class=\"text-muted ms-1\" *ngIf=\"label && value?.length\">{{label}}</label>\n\n<span class=\"float-end pointer me-2\" (click)=\"sortAlpha()\" *ngIf=\"canSortAlpha\"><fa-icon [icon]=\"['fal', 'sort-alpha-down']\"></fa-icon></span>\n\n<div class=\"sortable\"\n  cdkDropList\n  cdkDropListLockAxis=\"y\"\n  cdkDropListOrientation=\"vertical\"\n  [cdkDropListData]=\"value\"\n  [cdkDropListDisabled]=\"disabled || value.length < 2\"\n  (cdkDropListDropped)=\"onOrderChange($event)\">\n\n  <div #viewport class=\"papers-scrollbar viewport\" [style.height.px]=\"getViewportHeight()\">\n\n    <div class=\"token\"\n      cdkDrag\n      cdkDragBoundary=\".sortable\"\n      (click)=\"$event.stopPropagation()\"\n      *ngFor=\"let item of value; let i = index;\">\n\n      <div class=\"body\">\n        <rcp-token-field-editable-input\n          [ngModel]=\"item\"\n          (ngModelChange)=\"editValue = $event\"\n          [editable]=\"editIndex === i\"\n          [selectOnEdit]=\"true\"\n          [focusOnEdit]=\"true\"\n          [spellcheck]=\"false\"\n          (enter)=\"onEditEnter($event)\">\n        </rcp-token-field-editable-input>\n      </div>\n\n      <div class=\"addons\">\n        <span class=\"addon-btn\" *ngIf=\"editIndex === i\" (click)=\"saveEntry(i)\">\n          <fa-icon [icon]=\"['fas', 'check']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"canMoveToTop && editIndex != i && i != 0\" (click)=\"moveToTop(i)\">\n          <fa-icon [icon]=\"['fal', 'arrow-up']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"editIndex != i\" (click)=\"editEntry(i)\">\n          <fa-icon [icon]=\"['fal', 'edit']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"editIndex != i\" (click)=\"removeEntry(i)\">\n          <fa-icon [icon]=\"['fal', 'trash']\"></fa-icon>\n        </span>\n      </div>\n    </div>\n\n  </div>\n</div>\n\n<ng-container *ngIf=\"search; else simpleInput\">\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [ngbTypeahead]=\"search\"\n    [resultTemplate]=\"typeaheadResultTemplate\"\n    [focusFirst]=\"focusFirst\"\n    [editable]=\"true\"\n    [disabled]=\"disabled\"\n    [inputFormatter]=\"inputFormatter\"\n    (selectItem)=\"onSelectItem($event)\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [placeholder]=\"placeholder\">\n</ng-container>\n\n<ng-template #simpleInput>\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\">\n</ng-template>\n", styles: [":host{display:block;padding:3px 5px;height:auto!important;padding:3px 5px!important;min-height:66px}:host.disabled{opacity:.4}input{border:0;font-size:inherit;color:inherit;background-color:transparent;padding:5px 2px;width:100%;outline:none}label{font-size:smaller}.viewport{overflow-x:hidden;overflow-y:auto;width:100%}.token{display:flex;font-size:inherit;font-weight:inherit;padding:.5em .75em;margin:.25em .5em .25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:grab;transition:background-color .2s linear}.token input{padding:0}:host.disabled .token{cursor:default}.token .body{flex:1 1 auto;width:calc(100% - 80px)}.token .addon-btn{flex:1 0 auto;margin-left:.5em;cursor:pointer;display:none}.token:hover .addon-btn{display:inline}:host.disabled .token .addon-btn{cursor:not-allowed}.cdk-drag-preview{box-sizing:border-box;cursor:move;opacity:.6}.cdk-drag-placeholder{opacity:0}.cdk-drag-animating{transition:transform .25s cubic-bezier(0,0,.2,1)}.sortable.cdk-drop-list-dragging .cell:not(.cdk-drag-placeholder){transition:transform .25s cubic-bezier(0,0,.2,1)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i2$1.CdkDropList, selector: "[cdkDropList], cdk-drop-list", inputs: ["cdkDropListConnectedTo", "cdkDropListData", "cdkDropListOrientation", "id", "cdkDropListLockAxis", "cdkDropListDisabled", "cdkDropListSortingDisabled", "cdkDropListEnterPredicate", "cdkDropListSortPredicate", "cdkDropListAutoScrollDisabled", "cdkDropListAutoScrollStep"], outputs: ["cdkDropListDropped", "cdkDropListEntered", "cdkDropListExited", "cdkDropListSorted"], exportAs: ["cdkDropList"] }, { kind: "directive", type: i2$1.CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "component", type: i1$1.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "spin", "pulse", "mask", "styles", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "classes", "transform", "a11yRole"] }, { kind: "component", type: EditableComponent, selector: "rcp-token-field-editable-input", inputs: ["editable", "selectOnEdit", "focusOnEdit", "placeholder", "highlight", "transform", "parse", "spellcheck"], outputs: ["enter", "escape", "newLine"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TokenFieldEditableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rcp-token-field-editable', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TokenFieldEditableComponent),
                            multi: true
                        }
                    ], template: "\n<ng-template #typeaheadResultTemplate let-result=\"result\" let-term=\"term\">\n  <span>{{result}}</span>\n</ng-template>\n\n<label class=\"text-muted ms-1\" *ngIf=\"label && value?.length\">{{label}}</label>\n\n<span class=\"float-end pointer me-2\" (click)=\"sortAlpha()\" *ngIf=\"canSortAlpha\"><fa-icon [icon]=\"['fal', 'sort-alpha-down']\"></fa-icon></span>\n\n<div class=\"sortable\"\n  cdkDropList\n  cdkDropListLockAxis=\"y\"\n  cdkDropListOrientation=\"vertical\"\n  [cdkDropListData]=\"value\"\n  [cdkDropListDisabled]=\"disabled || value.length < 2\"\n  (cdkDropListDropped)=\"onOrderChange($event)\">\n\n  <div #viewport class=\"papers-scrollbar viewport\" [style.height.px]=\"getViewportHeight()\">\n\n    <div class=\"token\"\n      cdkDrag\n      cdkDragBoundary=\".sortable\"\n      (click)=\"$event.stopPropagation()\"\n      *ngFor=\"let item of value; let i = index;\">\n\n      <div class=\"body\">\n        <rcp-token-field-editable-input\n          [ngModel]=\"item\"\n          (ngModelChange)=\"editValue = $event\"\n          [editable]=\"editIndex === i\"\n          [selectOnEdit]=\"true\"\n          [focusOnEdit]=\"true\"\n          [spellcheck]=\"false\"\n          (enter)=\"onEditEnter($event)\">\n        </rcp-token-field-editable-input>\n      </div>\n\n      <div class=\"addons\">\n        <span class=\"addon-btn\" *ngIf=\"editIndex === i\" (click)=\"saveEntry(i)\">\n          <fa-icon [icon]=\"['fas', 'check']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"canMoveToTop && editIndex != i && i != 0\" (click)=\"moveToTop(i)\">\n          <fa-icon [icon]=\"['fal', 'arrow-up']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"editIndex != i\" (click)=\"editEntry(i)\">\n          <fa-icon [icon]=\"['fal', 'edit']\"></fa-icon>\n        </span>\n        <span class=\"addon-btn\" *ngIf=\"editIndex != i\" (click)=\"removeEntry(i)\">\n          <fa-icon [icon]=\"['fal', 'trash']\"></fa-icon>\n        </span>\n      </div>\n    </div>\n\n  </div>\n</div>\n\n<ng-container *ngIf=\"search; else simpleInput\">\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [ngbTypeahead]=\"search\"\n    [resultTemplate]=\"typeaheadResultTemplate\"\n    [focusFirst]=\"focusFirst\"\n    [editable]=\"true\"\n    [disabled]=\"disabled\"\n    [inputFormatter]=\"inputFormatter\"\n    (selectItem)=\"onSelectItem($event)\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\"\n    [placeholder]=\"placeholder\">\n</ng-container>\n\n<ng-template #simpleInput>\n  <input #input type=\"text\"\n    [(ngModel)]=\"text\"\n    (ngModelChange)=\"textChange.emit(text)\"\n    [disabled]=\"disabled\"\n    [placeholder]=\"placeholder\"\n    (keyup.enter)=\"onKeyUpEnter($event)\"\n    (keydown.enter)=\"onKeyDownEnter($event)\"\n    (blur)=\"onBlur($event)\">\n</ng-template>\n", styles: [":host{display:block;padding:3px 5px;height:auto!important;padding:3px 5px!important;min-height:66px}:host.disabled{opacity:.4}input{border:0;font-size:inherit;color:inherit;background-color:transparent;padding:5px 2px;width:100%;outline:none}label{font-size:smaller}.viewport{overflow-x:hidden;overflow-y:auto;width:100%}.token{display:flex;font-size:inherit;font-weight:inherit;padding:.5em .75em;margin:.25em .5em .25em .1em;border-radius:4px;-webkit-user-select:none;user-select:none;cursor:grab;transition:background-color .2s linear}.token input{padding:0}:host.disabled .token{cursor:default}.token .body{flex:1 1 auto;width:calc(100% - 80px)}.token .addon-btn{flex:1 0 auto;margin-left:.5em;cursor:pointer;display:none}.token:hover .addon-btn{display:inline}:host.disabled .token .addon-btn{cursor:not-allowed}.cdk-drag-preview{box-sizing:border-box;cursor:move;opacity:.6}.cdk-drag-placeholder{opacity:0}.cdk-drag-animating{transition:transform .25s cubic-bezier(0,0,.2,1)}.sortable.cdk-drop-list-dragging .cell:not(.cdk-drag-placeholder){transition:transform .25s cubic-bezier(0,0,.2,1)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], text: [{
                type: Input
            }], focusFirst: [{
                type: Input
            }], delimiter: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], label: [{
                type: Input
            }], maxVisibleElements: [{
                type: Input
            }], canMoveToTop: [{
                type: Input
            }], canSortAlpha: [{
                type: Input
            }], search: [{
                type: Input
            }, {
                type: Optional
            }], textChange: [{
                type: Output
            }], inputRef: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }], virtualScrollViewport: [{
                type: ViewChild,
                args: [CdkVirtualScrollViewport, { static: true }]
            }], viewport: [{
                type: ViewChild,
                args: ['viewport', { static: true }]
            }], focus: [{
                type: HostListener,
                args: ['click']
            }] } });

class ReadCubeTokenFieldModule {
    constructor(faConfig, faIconLibrary) {
        faConfig.defaultPrefix = 'fal';
        faIconLibrary.addIcons(faTimes, faEdit, faTrash, faCheck, faArrowUp, faSortAlphaDown, faMinusCircle, faPlusCircle);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTokenFieldModule, deps: [{ token: i1$1.FaConfig }, { token: i1$1.FaIconLibrary }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTokenFieldModule, declarations: [EditableComponent,
            TokenFieldComponent,
            TokenFieldPickerComponent,
            TokenFieldEditableComponent], imports: [CommonModule,
            FormsModule,
            DragDropModule,
            FontAwesomeModule], exports: [TokenFieldComponent,
            TokenFieldPickerComponent,
            TokenFieldEditableComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTokenFieldModule, imports: [CommonModule,
            FormsModule,
            DragDropModule,
            FontAwesomeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTokenFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        DragDropModule,
                        FontAwesomeModule
                    ],
                    declarations: [
                        EditableComponent,
                        TokenFieldComponent,
                        TokenFieldPickerComponent,
                        TokenFieldEditableComponent
                    ],
                    exports: [
                        TokenFieldComponent,
                        TokenFieldPickerComponent,
                        TokenFieldEditableComponent
                    ],
                    providers: []
                }]
        }], ctorParameters: () => [{ type: i1$1.FaConfig }, { type: i1$1.FaIconLibrary }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ReadCubeTokenFieldModule, TokenFieldComponent, TokenFieldEditableComponent, TokenFieldPickerComponent };
